import React,{useState,useEffect} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import axios from 'axios';
import {format,parseISO} from 'date-fns';

const {FiTrendingDown,FiPlus,FiFilter,FiEdit,FiTrash2,FiSave,FiX,FiCalendar,FiDollarSign,FiTag,FiCreditCard,FiFileText,FiChevronLeft,FiChevronRight,FiChevronsLeft,FiChevronsRight}=FiIcons;

const paymentMethods=['cash','card','bank_transfer'];

const Expenses=()=> {
  const [expenses,setExpenses]=useState([]);
  const [allExpenses,setAllExpenses]=useState([]);
  const [chartOfAccounts,setChartOfAccounts]=useState([]);
  const [groupedAccounts,setGroupedAccounts]=useState({});
  const [loading,setLoading]=useState(true);
  const [showModal,setShowModal]=useState(false);
  const [selectedExpense,setSelectedExpense]=useState(null);
  const [formData,setFormData]=useState({
    expense_date: format(new Date(),'yyyy-MM-dd'),
    chart_of_account_id: '',
    description: '',
    amount: '',
    payment_method: 'cash',
    vendor: ''
  });
  const [filters,setFilters]=useState({
    start_date: format(new Date(new Date().setDate(1)),'yyyy-MM-dd'),
    end_date: format(new Date(),'yyyy-MM-dd'),
    chart_of_account_id: ''
  });
  const [pagination,setPagination]=useState({currentPage: 1,totalPages: 1,itemsPerPage: 15,totalItems: 0});

  useEffect(()=> {
    fetchChartOfAccounts();
  },[]);

  useEffect(()=> {
    fetchExpenses();
  },[filters]);

  useEffect(()=> {
    updatePaginatedData();
  },[pagination.currentPage,allExpenses]);

  const fetchChartOfAccounts=async ()=> {
    try {
      const response=await axios.get('/api/settings/chart-of-accounts');
      setChartOfAccounts(response.data);
      const grouped=response.data.reduce((acc,account)=> {
        const {category}=account;
        if (!acc[category]) {
          acc[category]=[];
        }
        acc[category].push(account);
        return acc;
      },{});
      setGroupedAccounts(grouped);
      // Set default form account
      if (response.data.length > 0) {
        setFormData(prev=> ({...prev,chart_of_account_id: response.data[0].id}));
      }
    } catch (error) {
      console.error('Error fetching chart of accounts:',error);
    }
  };

  const fetchExpenses=async ()=> {
    try {
      setLoading(true);
      const params=new URLSearchParams(filters);
      const response=await axios.get(`/api/expenses?${params}`);
      setAllExpenses(response.data);
      updatePagination(response.data);
    } catch (error) {
      console.error('Error fetching expenses:',error);
    } finally {
      setLoading(false);
    }
  };

  const updatePagination=(data)=> {
    const totalItems=data.length;
    const totalPages=Math.max(1,Math.ceil(totalItems / pagination.itemsPerPage));
    setPagination(prev=> ({...prev,totalPages,totalItems,currentPage: prev.currentPage > totalPages ? 1 : prev.currentPage}));
  };

  const updatePaginatedData=()=> {
    const startIndex=(pagination.currentPage - 1) * pagination.itemsPerPage;
    const endIndex=startIndex + pagination.itemsPerPage;
    const paginatedExpenses=allExpenses.slice(startIndex,endIndex);
    setExpenses(paginatedExpenses);
  };

  const handlePageChange=(newPage)=> {
    if (newPage >=1 && newPage <=pagination.totalPages) {
      setPagination(prev=> ({...prev,currentPage: newPage}));
    }
  };

  const handleSubmit=async (e)=> {
    e.preventDefault();
    try {
      if (selectedExpense) {
        await axios.put(`/api/expenses/${selectedExpense.id}`,formData);
      } else {
        await axios.post('/api/expenses',formData);
      }
      setShowModal(false);
      fetchExpenses();
    } catch (error) {
      console.error('Error saving expense:',error);
    }
  };

  const openModal=(expense=null)=> {
    setSelectedExpense(expense);
    if (expense) {
      setFormData({
        expense_date: format(parseISO(expense.expense_date),'yyyy-MM-dd'),
        chart_of_account_id: expense.chart_of_account_id,
        description: expense.description,
        amount: expense.amount,
        payment_method: expense.payment_method,
        vendor: expense.vendor || ''
      });
    } else {
      setFormData({
        expense_date: format(new Date(),'yyyy-MM-dd'),
        chart_of_account_id: chartOfAccounts.length > 0 ? chartOfAccounts[0].id : '',
        description: '',
        amount: '',
        payment_method: 'cash',
        vendor: ''
      });
    }
    setShowModal(true);
  };

  const handleDelete=async (id)=> {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        await axios.delete(`/api/expenses/${id}`);
        fetchExpenses();
      } catch (error) {
        console.error('Error deleting expense:',error);
      }
    }
  };

  const totalExpenses=allExpenses.reduce((sum,expense)=> sum + parseFloat(expense.amount),0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-maroon-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-grey-900">Expenses</h1>
        <button onClick={()=> openModal()} className="bg-maroon-600 hover:bg-maroon-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2">
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span>Add Expense</span>
        </button>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center mb-4">
          <SafeIcon icon={FiFilter} className="w-5 h-5 text-grey-400 mr-2" />
          <h3 className="text-lg font-medium text-grey-900">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-grey-700 mb-1">Start Date</label>
            <input type="date" className="w-full px-3 py-2 border border-grey-300 rounded-lg" value={filters.start_date} onChange={(e)=> setFilters({...filters,start_date: e.target.value})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-grey-700 mb-1">End Date</label>
            <input type="date" className="w-full px-3 py-2 border border-grey-300 rounded-lg" value={filters.end_date} onChange={(e)=> setFilters({...filters,end_date: e.target.value})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-grey-700 mb-1">Account</label>
            <select className="w-full px-3 py-2 border border-grey-300 rounded-lg" value={filters.chart_of_account_id} onChange={(e)=> setFilters({...filters,chart_of_account_id: e.target.value})}>
              <option value="">All Accounts</option>
              {Object.entries(groupedAccounts).map(([category,accounts])=> (
                <optgroup label={category} key={category}>
                  {accounts.map(account=> (
                    <option key={account.id} value={account.id}>
                      {account.code} - {account.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div className="bg-maroon-50 p-4 rounded-lg text-center">
            <p className="text-sm font-medium text-maroon-700">Total Expenses</p>
            <p className="text-2xl font-bold text-maroon-900">QR {totalExpenses.toLocaleString()}</p>
          </div>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden rounded-md">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-grey-200">
            <thead className="bg-grey-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase">Account</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase">Method</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase">Recorded By</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-grey-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-grey-200">
              {expenses.map(expense=> (
                <motion.tr key={expense.id} initial={{opacity: 0}} animate={{opacity: 1}}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{format(parseISO(expense.expense_date),'MMM dd,yyyy')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="font-medium text-grey-800">{expense.account_name}</div>
                    <div className="text-xs text-grey-500">{expense.account_code}</div>
                  </td>
                  <td className="px-6 py-4 text-sm">{expense.description}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">QR {parseFloat(expense.amount).toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm capitalize">{expense.payment_method.replace('_',' ')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{expense.recorded_by_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-3">
                      <button onClick={()=> openModal(expense)} className="text-maroon-600 hover:text-maroon-900"><SafeIcon icon={FiEdit} className="w-4 h-4" /></button>
                      <button onClick={()=> handleDelete(expense.id)} className="text-red-600 hover:text-red-900"><SafeIcon icon={FiTrash2} className="w-4 h-4" /></button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 bg-grey-50 border-t border-grey-200 flex items-center justify-between">
            <div className="text-sm text-grey-700">Page {pagination.currentPage} of {pagination.totalPages}</div>
            <div className="flex space-x-2">
              <button onClick={()=> handlePageChange(1)} disabled={pagination.currentPage===1} className="p-2 rounded-md border border-grey-300"><SafeIcon icon={FiChevronsLeft} className="w-5 h-5" /></button>
              <button onClick={()=> handlePageChange(pagination.currentPage - 1)} disabled={pagination.currentPage===1} className="p-2 rounded-md border border-grey-300"><SafeIcon icon={FiChevronLeft} className="w-5 h-5" /></button>
              <button onClick={()=> handlePageChange(pagination.currentPage + 1)} disabled={pagination.currentPage===pagination.totalPages} className="p-2 rounded-md border border-grey-300"><SafeIcon icon={FiChevronRight} className="w-5 h-5" /></button>
              <button onClick={()=> handlePageChange(pagination.totalPages)} disabled={pagination.currentPage===pagination.totalPages} className="p-2 rounded-md border border-grey-300"><SafeIcon icon={FiChevronsRight} className="w-5 h-5" /></button>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div initial={{scale: 0.95}} animate={{scale: 1}} className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-medium mb-4">{selectedExpense ? 'Edit Expense' : 'Add Expense'}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Date</label>
                  <input type="date" required className="w-full px-3 py-2 border rounded-lg" value={formData.expense_date} onChange={e=> setFormData({...formData,expense_date: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Amount (QR)</label>
                  <input type="number" required min="0.01" step="0.01" className="w-full px-3 py-2 border rounded-lg" value={formData.amount} onChange={e=> setFormData({...formData,amount: e.target.value})} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Account</label>
                <select required className="w-full px-3 py-2 border rounded-lg" value={formData.chart_of_account_id} onChange={e=> setFormData({...formData,chart_of_account_id: e.target.value})}>
                  {Object.entries(groupedAccounts).map(([category,accounts])=> (
                    <optgroup label={category} key={category}>
                      {accounts.map(account=> (
                        <option key={account.id} value={account.id}>
                          {account.code} - {account.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea required rows="3" className="w-full px-3 py-2 border rounded-lg" value={formData.description} onChange={e=> setFormData({...formData,description: e.target.value})}></textarea>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Payment Method</label>
                  <select required className="w-full px-3 py-2 border rounded-lg" value={formData.payment_method} onChange={e=> setFormData({...formData,payment_method: e.target.value})}>
                    {paymentMethods.map(method=> <option key={method} value={method}>{method.replace('_',' ').toUpperCase()}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Vendor (Optional)</label>
                  <input type="text" className="w-full px-3 py-2 border rounded-lg" value={formData.vendor} onChange={e=> setFormData({...formData,vendor: e.target.value})} />
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={()=> setShowModal(false)} className="px-4 py-2 border rounded-lg">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-maroon-600 text-white rounded-lg"><SafeIcon icon={FiSave} className="inline w-4 h-4 mr-2" />Save</button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Expenses;